import { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  FormControlLabel,
  Switch,
  MenuItem,
} from '@mui/material';
import { useApi } from "../../hooks/useApi";
import {
  CRM_CONNECTOR_CAPABILITIES,
  CRM_CONNECTOR_DRIVERS,
  CRM_CONNECTOR_NATIVE_LABEL,
  CRM_CONNECTOR_PROXY_IP,
} from "../../constants/crmConnector";
import type { CrmConnectorCapabilityField, CrmConnectorDriverMetadataField } from "../../constants/crmConnector";
import CrmConnectorCapabilityList from "./CrmConnectorCapabilityList";
import CopyableValue from "./CopyableValue";

const emptyFormData = {
  driver: '',
  name: '',
  baseUrl: '',
  apiKey: '',
  sandboxBaseUrl: '',
  sandboxApiKey: '',
  markRefundedSupported: true,
  orderTagsSupported: true,
  recordChargebackSupported: true,
  useProxy: false,
  metadata: {} as Record<string, string>,
};

function toMetadataPayload(
  fields: readonly CrmConnectorDriverMetadataField[],
  values: Record<string, string>,
): Record<string, string | number> {
  return Object.fromEntries(
    fields
      .filter(field => values[field.key]?.trim())
      .map(field => [field.key, field.type === 'integer' ? Number(values[field.key]) : values[field.key].trim()]),
  );
}

interface CrmConnectorDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  apiUrl: string;
}

export default function CrmConnectorDialog({
  open,
  onClose,
  onSuccess,
  apiUrl,
}: CrmConnectorDialogProps) {
  const { post } = useApi();
  const [formData, setFormData] = useState(emptyFormData);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const driverOption = CRM_CONNECTOR_DRIVERS.find(option => option.value === formData.driver) ?? null;
  const baseUrlFixed = driverOption !== null && 'fixed' in driverOption.baseUrl;
  const baseUrlHelperText = driverOption !== null && 'perTenant' in driverOption.baseUrl
    ? driverOption.baseUrl.helperText
    : "The store's connector URL, e.g. https://crm.example.com/chargemont. A trailing /v1 is dropped.";
  const metadataFields = driverOption?.metadataFields ?? [];
  const metadataIncomplete = metadataFields.some(field => field.required && !formData.metadata[field.key]?.trim());
  const sandboxAvailable = driverOption === null || driverOption.hasSandbox;
  const unsupportedFields = CRM_CONNECTOR_CAPABILITIES
    .filter(capability => driverOption !== null && !driverOption.supportedOperations.includes(capability.operation))
    .map(capability => capability.field);

  const sandboxIncomplete = Boolean(formData.sandboxBaseUrl) !== Boolean(formData.sandboxApiKey);

  const resetAndClose = () => {
    setFormData(emptyFormData);
    setFormError(null);
    onClose();
  };

  const handleDialogClose = (_event: object, reason: string) => {
    if (reason !== 'backdropClick') {
      resetAndClose();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setFormError(null);
  };

  const handleDriverChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextOption = CRM_CONNECTOR_DRIVERS.find(option => option.value === e.target.value) ?? null;
    const capabilityFlags = Object.fromEntries(
      CRM_CONNECTOR_CAPABILITIES.map(capability => [
        capability.field,
        nextOption === null || nextOption.supportedOperations.includes(capability.operation),
      ]),
    );
    setFormData(prev => ({ ...prev, ...capabilityFlags, driver: e.target.value, baseUrl: '', sandboxBaseUrl: '', sandboxApiKey: '', metadata: {} }));
    setFormError(null);
  };

  const handleMetadataChange = (key: string, value: string) => {
    setFormData(prev => ({ ...prev, metadata: { ...prev.metadata, [key]: value } }));
    setFormError(null);
  };

  const handleProxyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, useProxy: e.target.checked }));
  };

  const toggle = (field: CrmConnectorCapabilityField) => {
    setFormData(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleCreate = async () => {
    setFormError(null);
    setSubmitting(true);

    const sandbox = formData.sandboxBaseUrl && formData.sandboxApiKey
      ? { sandboxBaseUrl: formData.sandboxBaseUrl, sandboxApiKey: formData.sandboxApiKey }
      : {};

    try {
      await post(apiUrl, {
        ...(formData.driver ? { driver: formData.driver } : {}),
        name: formData.name,
        ...(baseUrlFixed ? {} : { baseUrl: formData.baseUrl }),
        apiKey: formData.apiKey,
        ...sandbox,
        markRefundedSupported: formData.markRefundedSupported,
        orderTagsSupported: formData.orderTagsSupported,
        recordChargebackSupported: formData.recordChargebackSupported,
        useProxy: formData.useProxy,
        ...(metadataFields.length > 0 ? { metadata: toMetadataPayload(metadataFields, formData.metadata) } : {}),
      }, { slowApi: true });

      resetAndClose();
      await onSuccess();
    } catch (err: any) {
      setFormError(err.message || 'Failed to link CRM Connector account');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <DialogTitle>Link CRM Connector Account</DialogTitle>
      <DialogContent>
        {formError && (
          <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
            {formError}
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Account Name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            fullWidth
            required
            disabled={submitting}
            size="small"
          />
          {CRM_CONNECTOR_DRIVERS.length > 0 && (
            <TextField
              select
              label="CRM driver"
              value={formData.driver}
              onChange={handleDriverChange}
              fullWidth
              disabled={submitting}
              size="small"
            >
              <MenuItem value="">{CRM_CONNECTOR_NATIVE_LABEL}</MenuItem>
              {CRM_CONNECTOR_DRIVERS.map(option => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
          )}
          {!baseUrlFixed && (
            <TextField
              label="Base URL"
              name="baseUrl"
              value={formData.baseUrl}
              onChange={handleInputChange}
              helperText={baseUrlHelperText}
              fullWidth
              required
              disabled={submitting}
              size="small"
            />
          )}
          {metadataFields.map(field => (
            <TextField
              key={field.key}
              label={field.label}
              type={field.type === 'integer' ? 'number' : 'text'}
              value={formData.metadata[field.key] ?? ''}
              onChange={(e) => handleMetadataChange(field.key, e.target.value)}
              helperText={field.helperText}
              fullWidth
              required={field.required}
              disabled={submitting}
              size="small"
            />
          ))}
          <TextField
            label="API key"
            name="apiKey"
            type="password"
            value={formData.apiKey}
            onChange={handleInputChange}
            fullWidth
            required
            disabled={submitting}
            size="small"
          />
          <Box>
            <FormControlLabel
              control={<Switch name="useProxy" checked={formData.useProxy} onChange={handleProxyChange} disabled={submitting} />}
              label="Route requests through Chargemont's proxy"
            />
            <Typography component="div" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Ask the client to whitelist <CopyableValue text={CRM_CONNECTOR_PROXY_IP} copyTitle="Copy IP" />
            </Typography>
          </Box>
          {sandboxAvailable && (
            <>
              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Sandbox (optional)</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Used by the go-live check only. Leave empty if the client has no sandbox yet.
                </Typography>
              </Box>
              <TextField
                label="Sandbox base URL"
                name="sandboxBaseUrl"
                value={formData.sandboxBaseUrl}
                onChange={handleInputChange}
                error={sandboxIncomplete}
                fullWidth
                disabled={submitting}
                size="small"
              />
              <TextField
                label="Sandbox API key"
                name="sandboxApiKey"
                type="password"
                value={formData.sandboxApiKey}
                onChange={handleInputChange}
                error={sandboxIncomplete}
                helperText={sandboxIncomplete ? 'Enter both the sandbox URL and key, or neither' : undefined}
                fullWidth
                disabled={submitting}
                size="small"
              />
            </>
          )}
          <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Supported actions</Typography>
            <CrmConnectorCapabilityList values={formData} onToggle={toggle} disabled={submitting} disabledFields={unsupportedFields} />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={resetAndClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={submitting || sandboxIncomplete || metadataIncomplete || !formData.name || (!baseUrlFixed && !formData.baseUrl) || !formData.apiKey}
          sx={{
            backgroundColor: '#465fff',
            '&:hover': {
              backgroundColor: '#3641f5',
            },
          }}
        >
          {submitting ? 'Verifying...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
